import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMedplumProfile, useMedplum } from '@medplum/react'
import { Practitioner, PractitionerRole } from '@medplum/fhirtypes'
import { ScheduleContext } from './ScheduleContext'
import { filterStorageService } from '@/services/schedule-session-service'
import { logError } from '@/utils/errorUtils'
export interface ScheduleContextType {
  selectActivePractitionerProfile: React.Dispatch<React.SetStateAction<string | null>>
  isLoading: boolean
  activePractitionerProfile: Practitioner | null
  listOFPractitioners: Practitioner[]
  isStuffMember: boolean
  stuffProfile?: Practitioner | null
}
export const ScheduleContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activePractitionerProfile, setActivePractitionerProfile] = useState<Practitioner | null>(
    null,
  )
  const [isLoading, setIsLoading] = useState(false)
  const [isStuffMember, setIsStuffMember] = useState(false)
  const [stuffProfile, setStuffProfile] = useState<Practitioner | null>(null)
  const profile = useMedplumProfile()
  const medplum = useMedplum()
  const [searchParams] = useSearchParams()
  /**
   * `?practitioner=<id>` opens the calendar on one practitioner, so another screen can link
   * straight to a doctor's schedule — the Zocdoc ops page does, once a provider link is mapped.
   *
   * Session-only: it seeds the selection but is deliberately not written to the saved filter, so
   * following a link does not overwrite the multiselect an operator left set up for themselves.
   * Honoured only for a member who can see other calendars at all (`isStuffMember` below); for
   * anyone else `practitioner-policy` scopes Slots to their own compartment.
   */
  const requestedPractitionerId = searchParams.get('practitioner')
  const savedPractitionerIds = filterStorageService.get()?.practitionerIds || []

  const [listOFPractitioners, setListOfPractitioners] = useState<Practitioner[]>([])
  const [selectedPractitionerId, setSelectedPractitionerId] = useState<string | null>(null)
  const [selectedMultiplePractitionerIds, setSelectedMultiplePractitionerIds] = useState<string[]>(
    requestedPractitionerId ? [requestedPractitionerId] : savedPractitionerIds,
  )

  const checkForRole = async (practitionerId: string): Promise<PractitionerRole> => {
    const role = await medplum.searchOne(
      'PractitionerRole',
      `practitioner=Practitioner/${practitionerId}`,
    )
    if (role && role.code && role.code.length > 0) {
      const isStuffMemeberLoggedIn = role.code?.[0]?.coding?.[0]?.code === '303129000' || false
      setIsStuffMember(isStuffMemeberLoggedIn)
      if (isStuffMemeberLoggedIn) {
        setStuffProfile(profile as Practitioner)
        await loadAllPractitioners(role)
      } else {
        setStuffProfile(null)
        setActivePractitionerProfile(profile as Practitioner)
      }
      return role
    } else {
      setIsStuffMember(false)
      setStuffProfile(null)
      setActivePractitionerProfile(profile as Practitioner)
      throw new Error('No PractitionerRole found for the current user')
    }
  }
  const handleSetSelectedMultiplePractitionerIds = (ids: string[]) => {
    setSelectedMultiplePractitionerIds(ids)
    filterStorageService.set({ practitionerIds: ids })
  }

  // keep the setter shape expected by consumers (Dispatch<SetStateAction<string | null>>)
  const selectActivePractitionerProfile = setSelectedPractitionerId

  // when selectedPractitionerId changes, update the activePractitionerProfile
  useEffect(() => {
    if (!selectedPractitionerId) {
      setActivePractitionerProfile(null)
      return
    }
    const activePractitioner = listOFPractitioners.find(
      (item) => item.id === selectedPractitionerId,
    )
    if (activePractitioner) {
      setActivePractitionerProfile(activePractitioner)
    }
  }, [selectedPractitionerId, listOFPractitioners])

  const loadAllPractitioners = async (role: PractitionerRole) => {
    const practitionersRes = await medplum.search('PractitionerRole', {
      organization: role.organization?.reference,
      _include: 'PractitionerRole:practitioner',
      _count: 500,
      active: 'true',
    })

    // Here we have roles and practitioners, let's merge them
    //disable-next-line @typescript-eslint/no-explicit-any
    const profilesMap: {
      [key: string]: {
        roleData?: PractitionerRole | undefined
        profileData?: Practitioner | undefined
      }
    } = {}
    if (practitionersRes?.entry) {
      practitionersRes.entry
        .map((entry) => entry.resource)
        .forEach((resource) => {
          if (resource?.resourceType === 'PractitionerRole' && resource?.practitioner?.reference) {
            if (
              resource?.practitioner?.reference &&
              !Object.prototype.hasOwnProperty.call(profilesMap, resource.practitioner.reference)
            ) {
              profilesMap[resource.practitioner.reference] = { roleData: resource }
            } else {
              profilesMap[resource.practitioner.reference].roleData = resource
            }
            // disable-next-line @typescript-eslint/no-explicit-any
          } else if (String(resource?.resourceType) === 'Practitioner' && resource?.id) {
            if (!Object.prototype.hasOwnProperty.call(profilesMap, `Practitioner/${resource.id}`)) {
              profilesMap[`Practitioner/${resource.id}`] = {
                profileData: resource as unknown as Practitioner,
              }
            } else {
              profilesMap[`Practitioner/${resource.id}`].profileData =
                resource as unknown as Practitioner
            }
          }
        })
    }
    const listOfDoctors = Object.values(profilesMap)
      .filter(
        (item) =>
          item?.profileData?.resourceType === 'Practitioner' &&
          !['224579007', '303129000'].includes(item.roleData?.code?.[0]?.coding?.[0]?.code || ''),
      )
      .map((item) => ({ ...item.profileData }) as Practitioner)
    setListOfPractitioners(listOfDoctors)

    const hasSavedSelection = filterStorageService.get()?.practitionerIds !== undefined
    if (!hasSavedSelection) {
      setSelectedMultiplePractitionerIds(listOfDoctors.map((p) => p.id as string))
    }
  }

  useEffect(() => {
    if (!medplum || !profile || !profile.id) {
      return
    }
    setIsLoading(true)
    try {
      checkForRole(profile?.id).catch((error) => {
        logError('schedule', 'Failed to fetch PractitionerRole', error)
      })
    } catch (error) {
      logError('schedule', 'Failed to fetch current user', error)
    } finally {
      setIsLoading(false)
    }
  }, [medplum, profile])
  const value = useMemo(
    () => ({
      activePractitionerProfile: activePractitionerProfile,
      selectActivePractitionerProfile,
      isLoading,
      listOFPractitioners,
      isStuffMember,
      stuffProfile,
      selectedMultiplePractitionerIds,
      handleSetSelectedMultiplePractitionerIds,
    }),
    [
      activePractitionerProfile,
      selectActivePractitionerProfile,
      isLoading,
      listOFPractitioners,
      isStuffMember,
      stuffProfile,
      selectedMultiplePractitionerIds,
    ],
  )

  return <ScheduleContext.Provider value={value}>{children}</ScheduleContext.Provider>
}
